import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { InlineLoading } from '@carbon/react';

interface AuthGuardProps {
  children: React.ReactNode;
  requiredRole?: string;
  requiredPermissions?: string[];
  redirectTo?: string;
}

interface User {
  id: string;
  email: string;
  role: string;
  permissions: string[];
  accountType?: string;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requiredRole,
  requiredPermissions = [],
  redirectTo = '/login',
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Demo mode for staging environment - allow access without authentication
        if (import.meta.env.VITE_ENVIRONMENT === 'staging' && location.pathname.startsWith('/privacy')) {
          setUser({
            id: 'demo-user',
            email: 'demo@sigma08.com',
            role: requiredRole || 'user',
            permissions: requiredPermissions || [],
            accountType: 'demo',
          });
          setIsAuthenticated(true);
          return;
        }

        // Check for token in multiple locations for compatibility
        const token =
          localStorage.getItem('access_token') ||
          localStorage.getItem('auth_token') ||
          sessionStorage.getItem('access_token');

        if (!token) {
          setIsAuthenticated(false);
          return;
        }

        const response = await fetch('/api/v1/auth/me', {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          // Clear all possible token locations
          localStorage.removeItem('auth_token');
          localStorage.removeItem('access_token');
          sessionStorage.removeItem('access_token');
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [location.pathname]);

  if (isAuthenticated === null) {
    return (
      <div
        className="auth-guard-loading"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
        }}
      >
        <InlineLoading description="Verifying authentication..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (requiredRole && user?.role !== requiredRole) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requiredPermissions.length > 0) {
    const hasRequiredPermissions = requiredPermissions.every(permission => user?.permissions?.includes(permission));

    if (!hasRequiredPermissions) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
};
